import React, { useState } from 'react';
import useClosePopoverOnScroll from '../hooks/use-close-popover-on-scroll';
import { Like, Dislike } from '@saleshandy/icons';
import { accessibleOnClick } from '../../../../../shared/utils';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import {
  LeadRevealFeedbackSentiment,
  submitLeadFeedbackAPI,
} from '../../../helpers/leads.api';
import toaster from '../../../../../shared/toaster';
import DislikeFeedbackPopup from '../../profile-modal/dislike-feedback-popup';

interface CellFeedbackButtonsProps {
  leadId: number;
  initialSentiment?: 'positive' | 'negative';
  initialMessage?: string;
  onFeedbackSubmitted?: (
    sentiment: LeadRevealFeedbackSentiment,
    message?: string,
  ) => void;
  onPopoverOpenChange?: (open: boolean) => void;
  submitFn?: (
    entityId: number,
    sentiment: LeadRevealFeedbackSentiment,
    message?: string,
  ) => Promise<unknown>;
}

const CellFeedbackButtons: React.FC<CellFeedbackButtonsProps> = ({
  leadId,
  initialSentiment,
  initialMessage,
  onFeedbackSubmitted,
  onPopoverOpenChange,
  submitFn,
}) => {
  const [
    submittedSentiment,
    setSubmittedSentiment,
  ] = useState<LeadRevealFeedbackSentiment | null>(
    (initialSentiment as LeadRevealFeedbackSentiment) ?? null,
  );
  const [isDislikeOpen, setIsDislikeOpen] = useState(false);
  const [isScrollClosing, setIsScrollClosing] = useState(false);
  const [feedbackText, setFeedbackText] = useState(
    initialSentiment === 'negative' ? initialMessage ?? '' : '',
  );
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [lastSubmittedMessage, setLastSubmittedMessage] = useState<
    string | undefined
  >(
    initialSentiment === 'negative'
      ? initialMessage?.trim() || undefined
      : undefined,
  );

  const handleDislikeToggle = (open: boolean) => {
    setIsDislikeOpen(open);
    onPopoverOpenChange?.(open);
  };

  const sendFeedback = async (
    sentiment: LeadRevealFeedbackSentiment,
    message?: string,
  ) => {
    if (isFeedbackLoading) return;
    const trimmedMessage = message?.trim() || undefined;

    // Idempotency: skip API if nothing has changed
    if (
      sentiment === LeadRevealFeedbackSentiment.Positive &&
      submittedSentiment === LeadRevealFeedbackSentiment.Positive
    )
      return;
    if (
      sentiment === LeadRevealFeedbackSentiment.Negative &&
      submittedSentiment === LeadRevealFeedbackSentiment.Negative &&
      trimmedMessage === lastSubmittedMessage
    )
      return;

    setIsFeedbackLoading(true);
    try {
      await (submitFn
        ? submitFn(leadId, sentiment, trimmedMessage)
        : submitLeadFeedbackAPI({
            leadId,
            sentiment,
            ...(trimmedMessage && { message: trimmedMessage }),
          }));
      setSubmittedSentiment(sentiment);
      if (sentiment === LeadRevealFeedbackSentiment.Negative) {
        setLastSubmittedMessage(trimmedMessage);
      }
      onFeedbackSubmitted?.(sentiment, trimmedMessage);
      toaster.success('Thank you for your feedback');
    } catch {
      // silently ignore — axios interceptor handles generic errors
    } finally {
      setIsFeedbackLoading(false);
    }
  };

  useClosePopoverOnScroll(isDislikeOpen && !isScrollClosing, () => {
    setIsScrollClosing(true);
    const textToSubmit = feedbackText;
    setTimeout(() => {
      handleDislikeToggle(false);
      setIsScrollClosing(false);
      sendFeedback(LeadRevealFeedbackSentiment.Negative, textToSubmit);
    }, 200);
  });

  return (
    <div className="cell-feedback-btns">
      <OverlayTooltip placement={Placement.Bottom} text="Looks Accurate">
        <button
          type="button"
          className={`cell-feedback-btn cell-feedback-btn--like${
            submittedSentiment === LeadRevealFeedbackSentiment.Positive
              ? ' cell-feedback-btn--like-active'
              : ''
          }`}
          aria-label="Looks Accurate"
          disabled={isFeedbackLoading}
          {...accessibleOnClick(() =>
            sendFeedback(LeadRevealFeedbackSentiment.Positive),
          )}
        >
          <Like width={16} height={16} />
        </button>
      </OverlayTooltip>

      <OverlayPopover
        show={isDislikeOpen}
        placement={Placement.AutoEnd}
        rootClose
        onToggle={handleDislikeToggle}
        className={`profile-modal__dislike-popover${
          isScrollClosing ? ' profile-modal__dislike-popover--closing' : ''
        }`}
        content={
          <DislikeFeedbackPopup
            value={feedbackText}
            onChange={setFeedbackText}
            isLoading={isFeedbackLoading}
            onSubmit={async (message) => {
              await sendFeedback(LeadRevealFeedbackSentiment.Negative, message);
              setFeedbackText('');
              handleDislikeToggle(false);
            }}
            onClose={() => {
              setFeedbackText(
                submittedSentiment === LeadRevealFeedbackSentiment.Negative
                  ? lastSubmittedMessage ?? ''
                  : '',
              );
              handleDislikeToggle(false);
            }}
          />
        }
      >
        <span>
          {isDislikeOpen ? (
            <button
              type="button"
              className="cell-feedback-btn cell-feedback-btn--dislike cell-feedback-btn--dislike-active"
              aria-label="Something looks wrong"
              disabled={isFeedbackLoading}
              {...accessibleOnClick(() => handleDislikeToggle(false))}
            >
              <Dislike width={16} height={16} />
            </button>
          ) : (
            <OverlayTooltip
              placement={Placement.Bottom}
              text="Something looks wrong"
            >
              <button
                type="button"
                className={`cell-feedback-btn cell-feedback-btn--dislike${
                  submittedSentiment === LeadRevealFeedbackSentiment.Negative
                    ? ' cell-feedback-btn--dislike-active'
                    : ''
                }`}
                aria-label="Something looks wrong"
                disabled={isFeedbackLoading}
                {...accessibleOnClick(() => {
                  setFeedbackText(
                    submittedSentiment === LeadRevealFeedbackSentiment.Negative
                      ? lastSubmittedMessage ?? ''
                      : '',
                  );
                  handleDislikeToggle(true);
                })}
              >
                <Dislike width={16} height={16} />
              </button>
            </OverlayTooltip>
          )}
        </span>
      </OverlayPopover>
    </div>
  );
};

export default CellFeedbackButtons;
